// ---------------------------------------------------------------------------
// SUCHE-1 — die Vergleichsform einer Telefonnummer
// ---------------------------------------------------------------------------
// Befund 18.09.2026 (Marvin: «Funktion das man nach Telefonnummern suchen
// kann fehlt»; gemessen an 196 aktiven Kunden mit Nummer): gespeichert sind
// 79x «0xx xxx xx xx», 50x «0xxxxxxxxx», 41x «+41 xx xxx xx xx»,
// 15x «+41xxxxxxxxx», 9x «0041…», 2x anders. Die Suche verglich rohe Ziffern
// — «079 …» fand von 65 international gespeicherten Nummern genau eine,
// «+41 79 …» von 129 national gespeicherten keine.
//
// Die Vergleichsform: Schweizer Nummern national («0» + 9 Ziffern), egal ob
// mit +41, 0041, «(0)» oder der Fehl-Schreibweise «+41 081 …» erfasst;
// Auslandsnummern international mit «00» davor («+49 …» = «0049 …»), nur
// ohne Zeichen. Teilstuecke («34 56») bleiben ihre Ziffern.
//
// EINE Regel, zwei Ausfuehrende: die Schritt-Tabelle unten laeuft hier in
// JavaScript (Eingabe, Anzeige «warum dieser Treffer», Tests) und in
// Postgres (`src/lib/db/phoneCompareSql.ts`, die gespeicherte Nummer im
// WHERE). Wer die Regel aendert, aendert nur die Tabelle. Die Muster
// benutzen darum nur den gemeinsamen Teil beider Regex-Dialekte: [0-9],
// ^, $, ?, {n}, Klammer-Gruppen, Backslash vor Sonderzeichen — kein \d/\s
// (Postgres deutet sie nach Locale, JS nicht), keine Lookarounds.
// `suche1-phone-compare-form` bewacht das.

export interface PhoneCompareStep {
  readonly pattern: string;
  /** Alle Vorkommen (g) oder nur das erste. */
  readonly global: boolean;
  /** Ersatz aus Text-Stuecken und Gruppen-Nummern (1 = erste Klammer). */
  readonly replacement: ReadonlyArray<string | number>;
}

// Die Begruendungen stehen als Kommentar, nicht als Feld: die Tabelle liegt
// in jedem API-Bundle, und `api/system/all.js` steht an der ~3-MB-Wand.
export const PHONE_COMPARE_STEPS: readonly PhoneCompareStep[] = [
  // nur Ziffern, «+» und Klammern behalten (Leerzeichen, / - . und
  // Beschriftungen wie «Tel.» weg)
  { pattern: "[^0-9+()]", global: true, replacement: [] },
  // «(0)» ist die weggelassene Null: «+41 (0)79 …»
  { pattern: "\\(0\\)", global: true, replacement: [] },
  // «+» am Anfang heisst «00»
  { pattern: "^\\+", global: false, replacement: ["00"] },
  // restliche Zeichen weg — ab hier nur Ziffern
  { pattern: "[^0-9]", global: true, replacement: [] },
  // Schweiz international → national; eine Null direkt nach 41 ist die
  // Fehl-Schreibweise «+41 081 …»
  { pattern: "^00410?", global: false, replacement: ["0"] },
  // «41» ohne Plus nur bei GENAU einer vollstaendigen Nummer (41 + 9
  // Ziffern) — sonst ist es ein Teilstueck («41 22» vom Ende einer Nummer)
  { pattern: "^41([0-9]{9})$", global: false, replacement: ["0", 1] },
];

// JS-Ersatz: Gruppe als «$01» (zweistellig, damit eine folgende Ziffer nie
// als Teil der Gruppen-Nummer gelesen wird). Text-Stuecke sind nur Ziffern
// (vom Golden-Test erzwungen) und brauchen darum kein Escaping.
const JS_STEPS = PHONE_COMPARE_STEPS.map((step) => ({
  re: new RegExp(step.pattern, step.global ? "g" : ""),
  replacement: step.replacement
    .map((part) =>
      typeof part === "number" ? `$${String(part).padStart(2, "0")}` : part,
    )
    .join(""),
}));

/**
 * Die Vergleichsform einer Telefonnummer (siehe Kopf). Leer bei leerer
 * Eingabe oder einer Eingabe ohne Ziffern.
 */
export function phoneCompareForm(raw: string | null | undefined): string {
  if (!raw) return "";
  let value = raw;
  for (const step of JS_STEPS) value = value.replace(step.re, step.replacement);
  return value;
}

/**
 * Ab so vielen Ziffern ist etwas eine Telefonnummer (Auftrag SUCHE-1). In der
 * Suche: darunter bleibt eine Ziffern-Eingabe Stueck-Suche — Postleitzahl (4),
 * Hausnummer, bexio-Nummer (heute bis 355), Teilstuecke wie «34 56». Als
 * Erkennungsmerkmal eines Menschen (Lead → bestehender Kunde, Dubletten):
 * darunter ist es keins.
 */
export const PHONE_MIN_DIGITS = 6;

const PHONE_QUERY_CHARS = /^[0-9\s+/\-.()]+$/;

/** Wie viele Ziffern die Vergleichsform bzw. ein Text traegt. */
export function digitCount(value: string): number {
  return value.match(/[0-9]/g)?.length ?? 0;
}

/**
 * Ist die Eingabe EINE Telefonnummer? Nur Ziffern, Leerzeichen und
 * `+ / - . ( )`, mindestens {@link PHONE_MIN_DIGITS} Ziffern.
 */
export function isPhoneQuery(query: string): boolean {
  const trimmed = query.trim();
  if (!PHONE_QUERY_CHARS.test(trimmed)) return false;
  return digitCount(trimmed) >= PHONE_MIN_DIGITS;
}

/**
 * Die Nummer als Erkennungsmerkmal eines Menschen — gleiche Vergleichsform
 * heisst gleiche Nummer (Lead → bestehender Kunde, Dubletten-Vorschlag).
 * `null`, wenn sie weniger als {@link PHONE_MIN_DIGITS} Ziffern traegt: «+41»
 * allein oder «12» erkennen niemanden.
 */
export function phoneIdentityKey(
  raw: string | null | undefined,
): string | null {
  const form = phoneCompareForm(raw);
  return digitCount(form) >= PHONE_MIN_DIGITS ? form : null;
}
